/**
 * AI Equation Generator Component
 * Modal for generating LaTeX equations using AI from natural language or images
 */

import React, { useState, useRef } from 'react'
import { EditorView } from '@codemirror/view'
import { 
  XMarkIcon, 
  SparklesIcon, 
  PhotoIcon, 
  DocumentTextIcon,
  ArrowPathIcon 
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { getSelection } from '@/utils/latex/insertionHelpers'
import { aiAPI } from '@/api/ai'
import { chatAPI } from '@/api/chat'
import toast from 'react-hot-toast'

interface AIEquationGeneratorProps {
  view: EditorView | null
  theme?: 'light' | 'dark'
  documentId?: string
  onClose: () => void
  onInsert: (latexCode: string, equationType?: 'inline' | 'display' | 'numbered' | 'align') => void
}

type GenerationMode = 'text' | 'image'
type EquationType = 'inline' | 'display' | 'numbered' | 'align'

const AIEquationGenerator: React.FC<AIEquationGeneratorProps> = ({
  view,
  theme = 'light',
  documentId,
  onClose,
  onInsert
}) => {
  const [mode, setMode] = useState<GenerationMode>('text')
  const [equationType, setEquationType] = useState<EquationType>('display') // Default to display for AI-generated equations
  const [description, setDescription] = useState('')
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.type.startsWith('image/')) {
        setSelectedImage(file)
        const reader = new FileReader()
        reader.onload = (e) => {
          setImagePreview(e.target?.result as string)
        }
        reader.readAsDataURL(file)
      } else {
      }
    }
  }

  const handleGenerate = async () => {
    if (!view) return

    setIsGenerating(true)

    try {
      let requestText = ''
      let images: string[] | undefined = undefined

      if (mode === 'text') {
        if (!description.trim()) {
          setIsGenerating(false)
          return
        }
        requestText = description
      } else if (mode === 'image') {
        if (!selectedImage) {
          setIsGenerating(false)
          return
        }
        // Convert image to base64
        const base64 = await chatAPI.fileToBase64(selectedImage)
        images = [base64]
        requestText = 'Extract the equation from this image and generate LaTeX math code.'
      }

      const response = await aiAPI.generateEquation(
        requestText,
        images,
        undefined,
        documentId
      )

      if (response.success && response.result) {
        // Clean up the result (remove markdown code blocks if present)
        let latexCode = response.result.trim()
        if (latexCode.startsWith('```latex')) {
          latexCode = latexCode.replace(/^```latex\n?/, '').replace(/\n?```$/, '')
        } else if (latexCode.startsWith('```')) {
          latexCode = latexCode.replace(/^```\n?/, '').replace(/\n?```$/, '')
        }
        
        onInsert(latexCode, equationType)
        onClose()
      } else {
      }
    } catch (error: any) {
      console.error('AI equation generation error:', error)
    } finally {
      setIsGenerating(false)
    }
  }

  const dialogClass = clsx(
    'fixed inset-0 z-50 flex items-center justify-center',
    theme === 'dark' ? 'bg-black/50' : 'bg-black/30'
  )

  const contentClass = clsx(
    'relative rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto',
    theme === 'dark' ? 'bg-gray-800' : 'bg-white'
  )

  const inputClass = clsx(
    'w-full px-3 py-2 rounded-md border text-sm',
    theme === 'dark'
      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
  )

  const textareaClass = clsx(
    'w-full px-3 py-2 rounded-md border text-sm',
    theme === 'dark'
      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
    'min-h-[120px]'
  )

  return (
    <div className={dialogClass} onClick={onClose}>
      <div className={contentClass} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className={clsx(
            'absolute top-4 right-4 p-1 rounded-md',
            theme === 'dark' ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
          )}
        >
          <XMarkIcon className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 mb-4">
          <SparklesIcon className="w-5 h-5 text-primary-600" />
          <h3 className={clsx('text-lg font-semibold', theme === 'dark' ? 'text-gray-100' : 'text-gray-900')}>
            AI Equation Generator
          </h3>
        </div>

        {/* Mode Selection */}
        <div className="mb-4">
          <label className={clsx('block text-sm font-medium mb-2', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
            Generation Mode
          </label>
          <div className="flex gap-2">
            {([
              { value: 'text' as GenerationMode, label: 'Natural Language', icon: DocumentTextIcon },
              { value: 'image' as GenerationMode, label: 'From Image', icon: PhotoIcon }
            ]).map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => {
                  setMode(value)
                  setDescription('')
                  setSelectedImage(null)
                  setImagePreview(null)
                }}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                  mode === value
                    ? 'bg-primary-600 text-white'
                    : theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Equation Type Selection */}
        <div className="mb-4">
          <label className={clsx('block text-sm font-medium mb-2', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
            Equation Type
          </label>
          <div className="grid grid-cols-2 gap-2">
            {([
              { value: 'inline' as EquationType, label: 'Inline', desc: '$...$' },
              { value: 'display' as EquationType, label: 'Display', desc: '\\[...\\]' },
              { value: 'numbered' as EquationType, label: 'Numbered', desc: 'equation' },
              { value: 'align' as EquationType, label: 'Align', desc: 'align' }
            ]).map(({ value, label, desc }) => (
              <button
                key={value}
                onClick={() => setEquationType(value)}
                className={clsx(
                  'px-3 py-2 rounded-md text-sm font-medium transition-colors text-left',
                  equationType === value
                    ? 'bg-primary-600 text-white'
                    : theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
                title={desc}
              >
                <div className="font-semibold">{label}</div>
                <div className={clsx(
                  'text-xs mt-0.5',
                  equationType === value ? 'text-primary-100' : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                )}>
                  {desc}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Content Input */}
        <div className="mb-4">
          {mode === 'text' && (
            <>
              <label className={clsx('block text-sm font-medium mb-1', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                Describe the equation you want to create
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Integral of x squared from 0 to infinity, or Matrix multiplication of A and B, or Fourier transform of f(t)"
                className={textareaClass}
                rows={4}
              />
            </>
          )}

          {mode === 'image' && (
            <>
              <label className={clsx('block text-sm font-medium mb-1', theme === 'dark' ? 'text-gray-300' : 'text-gray-700')}>
                Upload equation image
              </label>
              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={clsx(
                    'w-full px-4 py-3 rounded-md border-2 border-dashed text-sm',
                    theme === 'dark'
                      ? 'border-gray-600 text-gray-300 hover:border-gray-500'
                      : 'border-gray-300 text-gray-600 hover:border-gray-400'
                  )}
                >
                  <PhotoIcon className="w-5 h-5 mx-auto mb-1" />
                  {selectedImage ? selectedImage.name : 'Click to select image'}
                </button>
                {imagePreview && (
                  <div className="mt-2">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="max-w-full max-h-64 rounded-md border border-gray-300 dark:border-gray-600"
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            disabled={isGenerating}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium',
              theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700',
              'disabled:opacity-50'
            )}
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={isGenerating}
            className="px-4 py-2 rounded-md text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isGenerating ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <SparklesIcon className="w-4 h-4" />
                Generate Equation
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AIEquationGenerator

